import type { JSX, ReactNode } from 'react';
import type { LegalDocument, LegalRenderContext } from '@kitup/brand';
import { myloreLegal } from './legal-data.js';

const { operator } = myloreLegal;
const legalConfig = myloreLegal;

/*
 * Тексты документов перенесены из `LegalPage.tsx` дословно: формулировки —
 * работа с юристом, а не часть разделения брендов. От оригинала отличается
 * ровно двумя вещами: обёртка `LegalShell` осталась в web-core (заголовок и
 * навигация одинаковы у всех брендов), а `Link` приходит через контекст —
 * пакет бренда не должен зависеть от роутера.
 */

function Intro({ children }: { readonly children: ReactNode }): JSX.Element {
  return <p className="mt-6 text-base leading-7">{children}</p>;
}

function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function ClauseList({
  items,
  unordered = false,
}: {
  readonly items: readonly string[];
  readonly unordered?: boolean;
}): JSX.Element {
  const List = unordered ? 'ul' : 'ol';
  return (
    <List>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </List>
  );
}

function DataTable({
  columns,
  rows,
}: {
  readonly columns: readonly string[];
  readonly rows: readonly (readonly string[])[];
}): JSX.Element {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[680px] border-collapse text-left text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} className="border border-slate-300 bg-slate-50 p-3 font-semibold">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${rowIndex}-${row[0] ?? ''}`}>
              {row.map((cell, cellIndex) => (
                <td
                  key={`${cellIndex}-${cell.slice(0, 24)}`}
                  className="border border-slate-300 p-3 align-top leading-6"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Offer({ Link }: LegalRenderContext): JSX.Element {
  return (
    <>
      <Intro>
        Настоящий документ является публичной офертой {operator.fullName} (далее — «Исполнитель»)
        заключить договор возмездного оказания услуг с дееспособным физическим лицом, достигшим 18
        лет и использующим сервис для личных, семейных и домашних нужд (далее — «Заказчик»).
      </Intro>

      <Section title="1. Термины и общие положения">
        <ClauseList
          items={[
            'Сервис — сайт mylore.ru, личный кабинет и программные средства «Милор».',
            'Книга — персонализированный цифровой результат, который в зависимости от тарифа может включать текст, иллюстрации, обложки, видео, озвучку, экранную версию и файл для печати.',
            'Материалы Заказчика — тексты, имена, фотографии, записи голоса, описания людей, мест и вещей, а также иные введённые или загруженные сведения.',
            'Тариф — показанные до оплаты состав услуги, цена, объём Книги, число включённых генераций и доступные форматы.',
            'Заказ — задание на создание одной Книги на условиях выбранного Тарифа.',
            'Аккаунт создаёт и Заказ оформляет только лицо, достигшее 18 лет. Сервис не предназначен для самостоятельного использования детьми: сведения о ребёнке предоставляет его законный представитель.',
            'Законодательство о защите прав потребителей применяется в полном объёме. Оферта не ограничивает права, которые нельзя ограничить договором.',
          ]}
        />
      </Section>

      <Section title="2. Предмет договора">
        <ClauseList
          items={[
            'Исполнитель по заданию Заказчика создаёт Книгу и предоставляет доступ к оплаченному результату в электронной форме, а Заказчик предоставляет необходимые материалы и оплачивает услугу.',
            'Индивидуальные условия определяются Офертой, выбранным Тарифом, заполненным заданием и подтверждённым бесплатным превью.',
            'Если прямо не указано иное, в услугу не входят печать и доставка физического экземпляра, передача исключительных прав, рекламное продвижение и бессрочное хранение исходников.',
          ]}
        />
      </Section>

      <Section title="3. Заключение договора и фиксация условий">
        <ClauseList
          items={[
            'До оплаты Заказчик может ознакомиться с Офертой, ценой, составом Тарифа, сроком исполнения и правилами возврата.',
            'Акцептом является подтверждение условий в интерфейсе и оплата Заказа. При отсутствии отдельного флажка акцептом является оплата при условии, что ссылка на Оферту была доступна до перехода к платёжной форме.',
            'Договор считается заключённым после подтверждения успешной оплаты платёжным оператором. Электронным доказательством служат сведения об аккаунте, Заказе, Тарифе, сумме, идентификаторе платежа и времени. Редакция Оферты записывается вместе с Заказом в момент создания платежа и не меняется задним числом.',
            'Согласия на фотографии, голос и распространение данных при публикации предоставляются отдельно и не считаются выданными посредством акцепта Оферты.',
          ]}
        />
      </Section>

      <Section title="4. Порядок исполнения">
        <ClauseList
          items={[
            'Заказчик создаёт аккаунт, выбирает Тариф, заполняет задание и предоставляет отдельные согласия до обработки фотографий или голоса.',
            'Исполнитель создаёт бесплатный превью-разворот. После его оценки Заказчик оплачивает продолжение работы.',
            'Платная генерация начинается автоматически после подтверждения оплаты, если до оплаты не указан иной момент.',
            'Заказчик проверяет правильность имён и исходных сведений и своевременно отвечает на запросы. Срок приостанавливается, когда без ответа или материала продолжить исполнение невозможно.',
          ]}
        />
      </Section>

      <Section title="5. Цена, расчёты и чек">
        <ClauseList
          items={[
            'Цена указывается в рублях, включает применимые налоги и не меняется для оплаченного Заказа без согласования нового объёма.',
            'Оплата проводится через ЮKassa или иного указанного партнёра. Исполнитель не получает полный номер карты, срок её действия и CVC.',
            'Кассовый чек формируется в момент оплаты с признаком полного расчёта и направляется в электронной форме по контакту, указанному при оплате. Заказчик отвечает за корректность контакта.',
            'Подписка и автоматические периодические списания в текущей версии не используются.',
          ]}
        />
      </Section>

      <Section title="6. Срок и передача результата">
        <ClauseList
          items={[
            `При корректных исходных материалах Книга создаётся ${legalConfig.deliveryTerm} после оплаты. Индивидуальный срок, показанный до оплаты, имеет приоритет.`,
            'Результат передаётся открытием доступа в личном кабинете и предоставлением предусмотренных Тарифом файлов или ссылки.',
            'Открытие доступа подтверждает передачу, но не лишает Заказчика права заявить о недостатках в установленные законом сроки.',
            'Заказчик обеспечивает совместимое устройство, актуальный браузер и интернет; Исполнитель сообщает известные существенные технические требования.',
          ]}
        />
      </Section>

      <Section title="7. Генеративные технологии и качество">
        <ClauseList
          items={[
            'Результат создаётся в том числе автоматизированно и носит творческий, вероятностный характер: возможны художественные отличия, вариативность и технические артефакты.',
            'Исполнитель не гарантирует фотографическое сходство, буквальное совпадение с субъективным ожиданием или уникальность стилистических элементов, если конкретный критерий не был согласован.',
            'Отсутствие оплаченного элемента, повреждённый файл, существенная ошибка в переданном Заказчиком имени и иное объективное несоответствие описанию рассматриваются как возможный недостаток.',
            'Творческие перегенерации по Тарифу не заменяют безвозмездное устранение недостатков, за которые отвечает Исполнитель.',
          ]}
        />
      </Section>

      <Section title="8. Материалы, изображения и голос">
        <ClauseList
          items={[
            'Заказчик гарантирует законность получения и передачи материалов, наличие полномочий законного представителя ребёнка или иного законного основания.',
            'Для материалов совершеннолетнего третьего лица Заказчик подтверждает наличие разрешения на передачу его данных и использование изображения или голоса в объёме Заказа.',
            'Запрещены незаконные материалы, нарушения частной жизни и интеллектуальных прав, сексуализированные изображения несовершеннолетних, травля, экстремистские материалы и инструкции к противоправным действиям.',
            'Исполнитель вправе приостановить обработку, запросить подтверждение прав, удалить явно незаконный материал или отказаться от исполнения в соответствующей части по закону.',
          ]}
        />
      </Section>

      <Section title="9. Интеллектуальные права">
        <ClauseList
          items={[
            'Права на Материалы Заказчика сохраняются за правообладателями. Заказчик предоставляет Исполнителю простое безвозмездное право использовать их только для исполнения Заказа, поддержки, устранения ошибок и выполнения закона.',
            'После оплаты Заказчик вправе хранить и читать Книгу, печатать экземпляры для личного некоммерческого использования и делиться предусмотренной Сервисом ссылкой.',
            'Коммерческое тиражирование, перепродажа, использование результата в рекламе, обучение на нём моделей и обход технических ограничений требуют отдельного соглашения, если иное не указано в Тарифе.',
            'Правовая охрана полностью автоматизированных элементов зависит от применимого права и обстоятельств создания; Исполнитель не гарантирует исключительное право на каждый такой элемент.',
          ]}
        />
      </Section>

      <Section title="10. Публикация по ссылке">
        <ClauseList
          items={[
            'По умолчанию Книга доступна только в аккаунте. Публичная ссылка создаётся отдельным действием Заказчика.',
            'Любой получатель ссылки может открыть её без авторизации и передать другим лицам.',
            'До публикации Заказчик получает необходимые разрешения изображённых и озвученных лиц. Публикация выполняется только после отдельного подтверждения согласия на распространение в интерфейсе; сама по себе готовность Книги публикацией не является.',
            'Отмена публикации закрывает дальнейший доступ по ссылке, но не удаляет копии, уже сохранённые третьими лицами вне Сервиса.',
          ]}
        />
        <p>
          <Link to="/legal/publication-consent">Согласие на публикацию персональной книги</Link>.
        </p>
      </Section>

      <Section title="11. Отказ, недостатки и возврат">
        <ClauseList
          items={[
            'Заказчик вправе отказаться от договора в любое время при оплате документально подтверждаемых фактических расходов, непосредственно связанных с конкретным Заказом.',
            'До запуска платной генерации сумма возвращается полностью. После запуска Исполнитель сообщает расчёт фактических расходов; фиксированный штраф за отказ не применяется.',
            'При недостатках Заказчик выбирает требования, предусмотренные законом: устранение, уменьшение цены, повторное оказание, возмещение расходов или отказ от договора в установленных случаях.',
            'Отсутствие договорной гарантии не сокращает законные сроки предъявления требований.',
          ]}
        />
        <p>
          Подробности: <Link to="/legal/payment">«Оплата, получение и возвраты»</Link>.
        </p>
      </Section>

      <Section title="12. Ответственность и форс-мажор">
        <ClauseList
          items={[
            'Стороны отвечают по законодательству РФ. Ограничения не применяются, когда закон запрещает ограничивать ответственность.',
            'Исполнитель не отвечает за законность материалов Заказчика, если не знал и не должен был знать о нарушении, и за распространение ссылки самим Заказчиком.',
            'Чрезвычайные и непредотвратимые обстоятельства освобождают от ответственности при своевременном уведомлении и принятии мер к уменьшению последствий. Обычный сбой подрядчика сам по себе не прекращает обязательства перед потребителем.',
          ]}
        />
      </Section>

      <Section title="13. Обращения и споры">
        <ClauseList
          items={[
            `Обращения направляются на ${operator.email} или по почтовому адресу Исполнителя с указанием email аккаунта, Книги, даты, суммы и требования.`,
            `Ответ направляется в специальный срок, установленный законом, а при его отсутствии — в течение ${legalConfig.responseTerm}.`,
            'Претензионный порядок добровольный, если закон не требует иного. Потребитель сохраняет установленный законом выбор подсудности.',
          ]}
        />
      </Section>

      <Section title="14. Действие и изменение Оферты">
        <ClauseList
          items={[
            'К Заказу применяется редакция, действовавшая в момент оплаты. Новая редакция не изменяет оплаченный Заказ без согласия Заказчика, кроме обязательных требований закона.',
            'Недействительность отдельного условия не влечёт недействительность остальных.',
            'Оферта действует до отзыва или замены новой редакцией.',
          ]}
        />
      </Section>
      <Requisites number="15" />
    </>
  );
}

function Privacy({ Link }: LegalRenderContext): JSX.Element {
  return (
    <>
      <Intro>
        Политика определяет порядок обработки данных пользователей {legalConfig.siteUrl}. Оператор —{' '}
        {operator.fullName}, ИНН {operator.inn}, ОГРНИП {operator.ogrnip}, адрес:{' '}
        {operator.postalAddress}, email: <a href={`mailto:${operator.email}`}>{operator.email}</a>.
      </Intro>

      <Section title="1. Область действия и принципы">
        <ClauseList
          items={[
            'Политика применяется к сайту, аккаунту, публичным страницам Книг, поддержке и связанным информационным системам.',
            'Данные обрабатываются законно, добросовестно, для определённых целей, без объединения несовместимых баз и без избыточного сбора.',
            'Оператор не принимает решений с юридическими последствиями исключительно автоматизированным способом.',
            'Сервис предназначен для совершеннолетних заказчиков; сведения детей предоставляют законные представители или лица с иным подтверждённым основанием.',
          ]}
        />
      </Section>

      <Section title="2. Субъекты и категории данных">
        <DataTable
          columns={['Субъект', 'Категории данных']}
          rows={[
            [
              'Пользователь и плательщик',
              'Имя, email, хэш пароля, язык, аккаунт, обращения, Заказы, идентификаторы, суммы и статусы платежей.',
            ],
            [
              'Ребёнок и персонажи',
              'Имя или псевдоним, возрастная группа, роль, описание внешности и характера, фотографии и сведения сюжета.',
            ],
            [
              'Владелец голоса',
              'Название профиля, аудиозапись, акустические и технические характеристики, ID профиля и синтетическая речь.',
            ],
            [
              'Посетитель сайта',
              'IP, дата и время, user-agent, технические журналы, необходимые cookie и локальные настройки.',
            ],
            [
              'Обратившийся в поддержку',
              'Контакты, переписка, вложения и данные для ответа и защиты прав.',
            ],
          ]}
        />
        <p>
          Оператор не получает полный номер карты, CVC и иные аутентификационные данные карты.
          Пользователь не должен вводить специальные категории данных в свободные поля без
          необходимости и отдельного согласования.
        </p>
      </Section>

      <Section title="3. Цели и основания обработки">
        <DataTable
          columns={['Цель', 'Данные', 'Основание']}
          rows={[
            [
              'Аккаунт и авторизация',
              'Имя, email, хэш пароля, токены, IP, user-agent.',
              'Заключение и исполнение договора; безопасность и законные интересы при соблюдении прав субъекта.',
            ],
            [
              'Создание и хранение Книги',
              'Задание, тексты, имена, фотографии, описания, изображения, видео и файлы.',
              'Исполнение договора, выгодоприобретателем по которому является ребёнок — герой Книги (пункт 5 части 1 статьи 6 152-ФЗ); для фотографий, голоса и данных иных лиц — отдельное согласие субъекта или его законного представителя.',
            ],
            [
              'Голос и озвучка',
              'Образец, профиль, синтетическая речь.',
              'Отдельное согласие владельца голоса или законного представителя.',
            ],
            [
              'Оплата и чеки',
              'Email, сумма, Заказ, ID и статус платежа, данные чека.',
              'Исполнение договора и обязанностей по налоговому и бухгалтерскому законодательству.',
            ],
            [
              'Публичная ссылка',
              'Выбранные сведения и материалы Книги.',
              'Отдельное согласие на распространение и действие «Опубликовать».',
            ],
            [
              'Поддержка и претензии',
              'Аккаунт, Заказ, переписка, доказательства.',
              'Договор, закон и осуществление прав сторон.',
            ],
            [
              'Безопасность',
              'IP, user-agent, события входа и журналы.',
              'Законные интересы в защите Сервиса при условии ненарушения прав субъекта.',
            ],
          ]}
        />
      </Section>

      <Section title="4. Источники, действия и способы">
        <ClauseList
          items={[
            'Данные поступают от пользователя, субъекта или представителя, автоматически от устройства, от платёжного партнёра и подрядчиков в связи с Заказом.',
            'Оператор осуществляет сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение, использование, передачу по поручению, обезличивание, блокирование, удаление и уничтожение автоматизированным и смешанным способом.',
            'Фотографии могут менять размер и формат, очищаться от метаданных и использоваться для производных изображений. Голос может очищаться, нормализоваться и преобразовываться для синтеза.',
          ]}
        />
      </Section>

      <Section title="5. Получатели и поручение обработки">
        <DataTable
          columns={['Обработчик', 'Что получает', 'Страна']}
          rows={[
            [
              'Timeweb Cloud — хостинг, база данных и объектное хранилище',
              'Все данные Сервиса: аккаунт, Заказы, фотографии, аудио, готовые Книги, журналы.',
              'Россия',
            ],
            [
              'ЮKassa — приём платежей и кассовые чеки',
              'Email плательщика, сумма, идентификатор Заказа, данные чека. Реквизиты карты вводятся на стороне ЮKassa и Оператору не передаются.',
              'Россия',
            ],
            [
              'Почтовый сервис отправки писем',
              'Email получателя и текст служебного письма.',
              'Россия',
            ],
            [
              'WaveSpeed AI — генерация изображений и видео, синтез речи',
              'Фотографии, текстовые описания внешности, образец голоса и производные материалы.',
              'Иностранная юрисдикция',
            ],
          ]}
        />
        <ClauseList
          items={[
            'Передаётся минимально необходимый объём. Поручение определяет цели, действия, конфиденциальность, безопасность, уведомление об инцидентах и удаление.',
            'Обработчикам запрещено использовать полученные материалы для собственных целей, в том числе для обучения моделей, рекламы и формирования каталогов.',
            'Платёжный партнёр самостоятельно обрабатывает данные, введённые на его странице; Оператор получает сведения для оплаты, чека и возврата.',
            'Мониторинг ошибок (Sentry) в текущей конфигурации выключен. Его включение изменяет состав получателей и отражается в настоящей Политике до начала передачи.',
            'Точные юридические наименования и адреса иностранных обработчиков указаны в уведомлении Оператора в Роскомнадзор и сообщаются по запросу на указанный ниже адрес.',
          ]}
        />
      </Section>

      <Section title="6. Локализация и трансграничная передача">
        <ClauseList
          items={[
            'Первичная запись, систематизация, накопление, хранение, уточнение и извлечение данных граждан РФ при интернет-сборе выполняются с использованием баз на территории РФ, кроме законных исключений.',
            'Генерация изображений, видео, речи и текста выполняется иностранными обработчиками, поэтому обработка включает трансграничную передачу. Её состав, получатели и страны перечислены в разделе 5.',
            'До начала такой передачи Оператор получает от иностранного получателя сведения о принимаемых мерах защиты, проводит оценку и направляет отдельное уведомление в Роскомнадзор по статье 12 152-ФЗ. Передача не осуществляется до направления уведомления, а также при запрете или ограничении со стороны Роскомнадзора.',
            'Страна, получатель, цель и состав данных сообщаются пользователю заранее — в разделе 5 и в отдельных согласиях на фотографии и голос.',
          ]}
        />
      </Section>

      <Section title="7. Фотографии, голос и биометрия">
        <ClauseList
          items={[
            'Фотографии и голос используются для художественной генерации, а не для аутентификации или установления личности и не сопоставляются с идентификационными базами.',
            'Если цель изменится и данные начнут использоваться для установления личности, до обработки будет получено согласие в форме, требуемой для биометрических персональных данных.',
            'Отдельные согласия применяются независимо от биометрической квалификации данных в конкретной ситуации.',
          ]}
        />
      </Section>

      <Section title="8. Распространение по публичной ссылке">
        <ClauseList
          items={[
            'До отдельной публикации Книга не предназначена для неопределённого круга лиц.',
            'Публичная ссылка может содержать имена, изображения, видео и голос. Пользователь отдельно выбирает публикацию и подтверждает полномочия по каждому субъекту.',
            'Субъект вправе определить категории данных, запреты и условия и потребовать прекращения распространения в установленные законом сроки.',
          ]}
        />
      </Section>

      <Section title="9. Сроки хранения и уничтожение">
        <DataTable
          columns={['Данные', 'Критерий хранения']}
          rows={[
            [
              'Аккаунт и активные Книги',
              'До удаления аккаунта, прекращения договора или утраты необходимости для целей.',
            ],
            [
              'Фотографии, голос и профили',
              `До достижения цели, удаления материала или отзыва; уничтожение — не позднее ${legalConfig.purgeTerm}, если нет иного законного основания.`,
            ],
            ['Публичная Книга', 'До отмены публикации, требования субъекта или удаления аккаунта.'],
            [
              'Заказы, платежи, чеки и претензии',
              'В течение обязательных сроков хранения налоговых, бухгалтерских и доказательственных документов.',
            ],
            [
              'Резервные копии',
              'До плановой перезаписи; восстановленные данные вновь подчиняются ограничениям и удалению.',
            ],
            [
              'Технические журналы',
              'Не дольше необходимого для безопасности, расследования инцидентов и защиты прав.',
            ],
            [
              'Запись об уничтожении',
              `Адрес аккаунта, дата, категории и хэши уничтоженных файлов, адрес последнего входа — ${legalConfig.destructionRecordTerm} после уничтожения, чтобы ответить на обращение, поступившее после удаления аккаунта. Ни изображений, ни имён в записи нет.`,
            ],
          ]}
        />
        <p>
          При удалении аккаунта доступ блокируется без неоправданной задержки. Обязательно хранимые
          сведения изолируются от продуктовой обработки и удаляются после прекращения основания.
        </p>
        <p>
          Удаление аккаунта запускается сразу, а физическое уничтожение файлов — через{' '}
          {legalConfig.quarantineTerm}: этот срок даёт время на обращение, если с аккаунтом что-то
          не так, и в эти дни аккаунт уже закрыт и вход невозможен. Если аккаунт заблокирован
          администратором, данные сохраняются на срок, требуемый законом для ответа на обращение или
          запрос.
        </p>
      </Section>

      <Section title="10. Защита и инциденты">
        <p>
          Применяются разграничение доступа, стойкое хэширование паролей, шифрование каналов,
          приватное хранилище, временные ссылки, резервирование, журналирование, управление
          уязвимостями и иные меры с учётом угроз. При инциденте Оператор локализует его,
          документирует последствия и уведомляет Роскомнадзор и субъектов, когда этого требует
          закон.
        </p>
      </Section>

      <Section title="11. Права субъекта и обращения">
        <ClauseList
          items={[
            'Субъект вправе получать сведения об обработке, требовать уточнения, блокирования или уничтожения незаконных или неточных данных, отзывать согласие, прекращать распространение и обжаловать действия.',
            `Запрос направляется на ${operator.email} или почтовый адрес с данными для идентификации, описанием связи с Оператором и требованием.`,
            'Оператор вправе запросить разумное подтверждение личности и полномочий без избыточного сбора и отвечает в сроки 152-ФЗ.',
            'Отзыв не влияет на законность прежней обработки и не прекращает обработку, для которой сохраняется самостоятельное основание.',
          ]}
        />
      </Section>

      <Section title="12. Cookie, изменения и контакты">
        <p>
          Технические средства описаны в <Link to="/legal/cookies">Уведомлении о cookie</Link>.
          Существенные изменения целей не применяются задним числом; при необходимости запрашивается
          новое согласие. Вопросы принимаются на {operator.email}.
        </p>
      </Section>
      <LegalSources number="13" />
    </>
  );
}

function PhotoConsent({ Link }: LegalRenderContext): JSX.Element {
  return (
    <>
      <Intro>
        Устанавливая отдельный флажок до загрузки фотографий, пользователь даёт {operator.fullName}{' '}
        конкретное, предметное, информированное и однозначное согласие и подтверждает полномочия в
        отношении изображённых лиц.
      </Intro>
      <Section title="1. Субъект и полномочия">
        <ClauseList
          items={[
            'В отношении собственного изображения пользователь действует от своего имени.',
            'В отношении ребёнка пользователь подтверждает статус родителя, усыновителя, опекуна или иного законного представителя и обязанность действовать в интересах ребёнка.',
            'В отношении совершеннолетнего третьего лица пользователь подтверждает доказуемое разрешение на передачу данных и предоставляет основание по запросу.',
            'Заказ вправе оформлять только лицо старше 18 лет.',
          ]}
        />
      </Section>
      <Section title="2. Данные и цели">
        <p>
          Обрабатываются фотография, черты внешности, примерный возраст, имя или псевдоним, роль,
          описание одежды и характера, метаданные и созданные персонажи, иллюстрации, обложки и
          видео. Цели: оценка пригодности фото, создание и согласование персонажа, генерация Книги,
          хранение результата, поддержка, претензии и безопасность. Фото не используется для
          рекламы, распознавания лица, аутентификации, поиска личности или обучения собственной
          универсальной модели.
        </p>
      </Section>
      <Section title="3. Действия и способы">
        <p>
          Разрешаются сбор, запись, хранение, уточнение, просмотр уполномоченными лицами, изменение
          размера и формата, кадрирование, удаление EXIF, временные копии, производные материалы,
          передача обработчику генерации, получение результата, блокирование и уничтожение
          автоматизированным и смешанным способом.
        </p>
      </Section>
      <Section title="4. Обработчики и иностранная передача">
        <ClauseList
          items={[
            'Файлы хранятся приватно в российском объектном хранилище и передаются провайдеру генерации только для конкретной операции.',
            'Генерация изображений и видео выполняется иностранным обработчиком (WaveSpeed AI), поэтому фотография и производные от неё материалы передаются за пределы Российской Федерации. Перечень обработчиков и стран приведён в разделе 5 Политики.',
            'До начала такой передачи Оператор выполняет требования статьи 12 152-ФЗ, включая направление отдельного уведомления в Роскомнадзор. Передача не осуществляется при запрете или ограничении.',
            'Обработчик не получает самостоятельного права на публикацию, рекламу или иное использование вне поручения.',
          ]}
        />
      </Section>
      <Section title="5. Изображение и публикация">
        <p>
          Согласие разрешает непубличную обработку для Заказа, но не доступ неопределённого круга
          лиц. Публичная ссылка требует отдельного{' '}
          <Link to="/legal/publication-consent">согласия</Link>. Пользователь подтверждает наличие
          разрешения на использование изображения по статье 152.1 ГК РФ.
        </p>
      </Section>
      <Section title="6. Срок, отзыв и последствия">
        <ClauseList
          items={[
            'Согласие действует до достижения целей, удаления материала или аккаунта либо отзыва — что наступит раньше.',
            `Отзыв выполняется в аккаунте или письмом на ${operator.email}; новые операции прекращаются после обработки отзыва.`,
            `Данные уничтожаются не позднее ${legalConfig.purgeTerm}, если нет иного законного основания для временного хранения.`,
            'Отзыв может сделать невозможными новые генерации и восстановление результата, но не отменяет законность прежней обработки.',
          ]}
        />
      </Section>
      <Section title="7. Фиксация согласия">
        <p>
          Факт подтверждается записью, связанной с аккаунтом, датой и временем. Текст доступен до
          подтверждения. Согласие отдельно от <Link to="/legal/offer">Оферты</Link> и{' '}
          <Link to="/legal/privacy">Политики</Link>.
        </p>
      </Section>
    </>
  );
}

function VoiceConsent(): JSX.Element {
  return (
    <>
      <Intro>
        Устанавливая отдельный флажок до создания голосового профиля, пользователь даёт{' '}
        {operator.fullName} согласие на обработку записи и создание синтетической речи.
      </Intro>
      <Section title="1. Владелец голоса и полномочия">
        <ClauseList
          items={[
            'Пользователь подтверждает, что голос принадлежит ему, он является законным представителем ребёнка либо имеет доказуемое разрешение совершеннолетнего владельца на передачу, создание профиля и озвучивание Книги.',
            'Запрещено загружать голос публичного или иного третьего лица без разрешения и использовать результат для мошенничества, дискредитации, обхода аутентификации, ложных официальных заявлений или введения в заблуждение.',
          ]}
        />
      </Section>
      <Section title="2. Данные, цель и действия">
        <p>
          Обрабатываются имя профиля, запись, тембр, интонационные и технические характеристики,
          идентификатор профиля и синтетическая речь. Цель — озвучивание выбранных пользователем
          Книг. Разрешаются загрузка, хранение, очистка, нормализация, преобразование, выделение
          признаков, передача провайдеру, создание профиля, синтез, блокирование и уничтожение.
        </p>
      </Section>
      <Section title="3. Ограничения и риски">
        <ClauseList
          items={[
            'Голос не используется для идентификации, входа, рекламы, материалов других пользователей, продажи профиля или обучения собственной универсальной модели.',
            'Синтетическая речь может отличаться от оригинала и восприниматься как голос реального человека; пользователь учитывает этот риск при распространении.',
            'Озвучка не публикуется без отдельного действия пользователя и согласия на публикацию.',
          ]}
        />
      </Section>
      <Section title="4. Обработчики и трансграничная передача">
        <p>
          Может привлекаться внешний, в том числе иностранный, провайдер. До передачи Оператор
          выполняет требования к поручению и трансграничной передаче и сообщает получателя, страну,
          цель и состав данных. Передача не проводится при запрете или отсутствии требуемого
          согласия.
        </p>
      </Section>
      <Section title="5. Биометрическая квалификация">
        <p>
          Оператор не использует голос для установления личности. Если цель изменится, до
          биометрической идентификации будет получено согласие в письменной или иной требуемой
          форме. Этот документ не является согласием на голосовую биометрическую идентификацию.
        </p>
      </Section>
      <Section title="6. Срок, отзыв и уничтожение">
        <ClauseList
          items={[
            'Согласие действует до удаления профиля или аккаунта, достижения цели либо отзыва.',
            `Отзыв направляется на ${operator.email} или выполняется в аккаунте; после него новые озвучки не создаются.`,
            `Образец, активный профиль и доступные копии удаляются либо их удаление запрашивается у обработчика не позднее ${legalConfig.purgeTerm}, если нет иного основания.`,
            'Ранее созданная озвучка удаляется с Книгой или по отдельному требованию, когда хранение больше не нужно для договора.',
          ]}
        />
      </Section>
      <Section title="7. Фиксация согласия">
        <p>
          Подтверждение связывается с аккаунтом, голосовым профилем, датой и временем. Оно не
          заменяет согласие на публикацию и отзывается независимо от Оферты.
        </p>
      </Section>
    </>
  );
}

function PublicationConsent(): JSX.Element {
  return (
    <>
      <Intro>
        Документ применяется при действии «Опубликовать». Ссылка доступна без авторизации и может
        позволить неопределённому кругу лиц просматривать и сохранять материалы Книги.
      </Intro>
      <Section title="1. Отдельный характер согласия">
        <p>
          Согласие на распространение предоставляется отдельно от Оферты, Политики и непубличной
          обработки фотографий и голоса. Молчание, заранее проставленный флажок или создание Книги
          не являются согласием.
        </p>
      </Section>
      <Section title="2. Возможный состав публикации">
        <ClauseList
          unordered
          items={[
            'название и текст Книги;',
            'имена, псевдонимы, возрастные и ролевые сведения персонажей;',
            'созданные по фотографиям изображения и черты внешности;',
            'обложки, иллюстрации, видео и анимация;',
            'озвучка синтетическим или иным выбранным голосом.',
          ]}
        />
        <p>
          Исходные фотографии и образец голоса не должны открываться по ссылке, если пользователь не
          включил их в Книгу и отдельно не разрешил публикацию.
        </p>
      </Section>
      <Section title="3. Условия распространения">
        <ClauseList
          items={[
            'Доступ предоставляется по непредсказуемой, но не секретной ссылке; получатель может переслать её.',
            'Получатели могут просматривать Книгу для личных целей, но не получают права коммерчески использовать изображения, голос или материалы.',
            'Оператор не включает Книгу в каталог и не использует её в рекламе без нового согласия.',
            'Получатель может записать экран или сохранить файлы; после получения третьим лицом Оператор не способен полностью контролировать такие копии.',
          ]}
        />
      </Section>
      <Section title="4. Полномочия, запреты и условия">
        <p>
          Пользователь подтверждает статус субъекта или законного представителя ребёнка, а для
          других лиц — наличие достаточного согласия на обнародование изображения, голоса и данных.
          До публикации субъекту должна быть доступна возможность определить разрешённые категории,
          запреты и условия дальнейшей обработки. Если Сервис не может их выполнить, соответствующие
          данные исключаются либо Книга не публикуется.
        </p>
      </Section>
      <Section title="5. Срок и прекращение">
        <ClauseList
          items={[
            'Согласие действует до отмены публикации или требования субъекта.',
            `Пользователь отключает ссылку в аккаунте; субъект может написать на ${operator.email}, указав себя, контакт, Книгу и перечень данных.`,
            'Доступ прекращается в установленный законом срок. Это не удаляет ранее сохранённые третьими лицами копии, но Оператор прекращает собственное распространение.',
          ]}
        />
      </Section>
      <Section title="6. Фиксация согласия">
        <p>
          Подтверждение даётся отдельным флажком рядом с кнопкой «Опубликовать» и фиксируется по
          конкретной Книге: сохраняются аккаунт, идентификатор Книги, дата, время и редакция
          настоящего документа. Флажок не проставляется заранее и снимается после каждой публикации:
          снятие Книги с публикации и повторное открытие ссылки требуют нового подтверждения,
          поскольку состав раскрываемых данных мог измениться.
        </p>
      </Section>
    </>
  );
}

function Payment({ Link }: LegalRenderContext): JSX.Element {
  return (
    <>
      <Intro>
        Правила являются частью <Link to="/legal/offer">Оферты</Link> и применяются к разовым
        платежам за Книгу и дополнительные пакеты генераций.
      </Intro>
      <Section title="1. Цена и платёж">
        <ClauseList
          items={[
            'Цена, валюта, Тариф и состав Заказа показываются до перехода в ЮKassa.',
            'Реквизиты способа оплаты вводятся на стороне платёжного партнёра и не хранятся Сервисом.',
            'Услуга считается оплаченной только после серверной проверки успешного статуса.',
            'При сбое следует проверить первый платёж до повторной попытки; при двойном списании пользователь сообщает оба идентификатора.',
          ]}
        />
      </Section>
      <Section title="2. Кассовый чек">
        <p>
          Чек формируется в момент оплаты с признаком полного расчёта: платная генерация запускается
          сразу после подтверждения платежа, а доступ к результату открывается в личном кабинете.
          Для чека используются контакт, наименование услуги, сумма и параметры расчёта; чек
          направляется в электронной форме по контакту, указанному при оплате. Об ошибке в чеке
          следует сообщить на {operator.email}.
        </p>
      </Section>
      <Section title="3. Начало, срок и получение">
        <ClauseList
          items={[
            'Платная генерация запускается автоматически после оплаты; оформляя Заказ, пользователь просит начать исполнение без дополнительного ожидания.',
            `При корректных материалах результат предоставляется ${legalConfig.deliveryTerm}, если до оплаты не показан иной срок.`,
            'При просрочке применяются права потребителя по статье 28 Закона РФ «О защите прав потребителей».',
            'Книга открывается в аккаунте; ссылка, PDF, печатный файл, видео и озвучка входят только при указании в Тарифе. Проверка результата не прекращает законные права.',
          ]}
        />
      </Section>
      <Section title="4. Отказ пользователя">
        <ClauseList
          items={[
            'Пользователь вправе отказаться в любое время. До запуска платной генерации возвращается вся сумма.',
            'После запуска удерживаются только документально подтверждаемые расходы по конкретному Заказу: оплаченные вызовы генерации, реально выполненная индивидуальная работа и иные неизбежные затраты.',
            'Общие расходы, упущенная выгода, штраф за отказ и стоимость невыполненной части не считаются фактическими расходами.',
            'Исполнитель предоставляет расчёт удержания по запросу; остаток возвращается тем же способом, если иной способ не согласован законно.',
          ]}
        />
      </Section>
      <Section title="5. Недостатки">
        <ClauseList
          items={[
            'Пользователь выбирает предусмотренное законом требование: устранение, уменьшение цены, повторное оказание, возмещение расходов или отказ в установленных случаях.',
            'Исправление объективного недостатка не уменьшает пакет творческих перегенераций.',
            'Требования предъявляются при приёмке, в ходе исполнения и после передачи в законные сроки; сокращённый семидневный срок не устанавливается.',
            'Художественное предпочтение оценивается по описанию Тарифа, заданию, превью и обычно предъявляемым требованиям и не всегда является недостатком.',
          ]}
        />
      </Section>
      <Section title="6. Порядок требования">
        <ClauseList
          items={[
            `Напишите с email аккаунта на ${operator.email}.`,
            'Укажите ФИО, Книгу, дату, сумму, по возможности ID платежа, требование и обстоятельства; приложите подтверждающие файлы, но не реквизиты карты, CVC или пароль.',
            'Для требований, подпадающих под статью 31 Закона РФ «О защите прав потребителей», срок удовлетворения составляет 10 дней.',
            'Отказ должен быть мотивирован. Срок фактического зачисления после инициирования возврата зависит от банка и платёжной системы.',
          ]}
        />
      </Section>
    </>
  );
}

function Cookies(): JSX.Element {
  return (
    <>
      <Intro>
        Сайт использует только технические cookie и настройки браузера. В текущей версии нет ни
        аналитики, ни рекламных сетей, ни счётчиков посещаемости, ни межсайтового отслеживания:
        страницы Сервиса не загружают стороннего кода, кроме файлов собственного хранилища и
        платёжной страницы партнёра, открываемой по действию пользователя.
      </Intro>
      <Section title="1. Используемые cookie">
        <DataTable
          columns={['Название', 'Назначение', 'Срок и свойства']}
          rows={[
            [
              'kitup_refresh',
              'Восстановление сессии и ротация токена.',
              'До 30 дней; HttpOnly, Secure в production, SameSite=Lax.',
            ],
            [
              'kitup_csrf',
              'Защита действий аккаунта от поддельных запросов.',
              'До 30 дней; Secure в production, SameSite=Lax; читается приложением для защитного заголовка.',
            ],
          ]}
        />
        <p>
          Cookie платёжной страницы устанавливаются на домене партнёра и регулируются его
          документами. Точные имена технических cookie могут меняться без изменения назначения.
        </p>
      </Section>
      <Section title="2. Локальное хранилище">
        <DataTable
          columns={['Ключ', 'Назначение', 'Передача серверу']}
          rows={[
            ['kitup:locale', 'Выбранный язык.', 'Автоматически не передаётся.'],
            [
              'kitup:narration-muted:<ID книги>',
              'Настройка звука конкретной Книги.',
              'Автоматически не передаётся.',
            ],
          ]}
        />
        <p>Access-токен хранится только в памяти вкладки и не записывается в Local Storage.</p>
      </Section>
      <Section title="3. Основание и управление">
        <ClauseList
          items={[
            'Технологии необходимы для функций, запрошенных самим пользователем, и для безопасности аккаунта. Правовое основание — исполнение договора и осуществление прав Оператора (пункты 5 и 7 части 1 статьи 6 152-ФЗ); отдельное согласие для них не требуется.',
            'Баннер с выбором «принять или отклонить» не показывается: отклонять нечего — необязательных технологий на сайте нет. Он появится одновременно с первой такой технологией, и до выбора пользователя она устанавливаться не будет.',
            'Блокировка обязательных cookie в браузере нарушит авторизацию: войти в аккаунт будет невозможно.',
            'Пользователь может удалить cookie и Local Storage в браузере; потребуется новый вход, а настройки вернутся к значениям по умолчанию.',
            'Выход аннулирует серверную сессию и удаляет сессионные cookie в браузере.',
            'Необязательные аналитические, рекламные или персонализирующие технологии не устанавливаются без отдельного выбора и правового основания.',
          ]}
        />
      </Section>
      <Section title="4. Изменения">
        <p>
          Список пересматривается при изменении сайта. Подключение необязательных технологий не
          маскируется под обновление уведомления и требует самостоятельного основания.
        </p>
      </Section>
    </>
  );
}

function Requisites({ number }: { readonly number: string }): JSX.Element {
  return (
    <Section title={`${number}. Реквизиты Исполнителя`}>
      <p>
        {operator.fullName}
        <br />
        ИНН {operator.inn}
        <br />
        ОГРНИП {operator.ogrnip}
        <br />
        Система налогообложения: {operator.taxSystem}
        <br />
        Email: {operator.email}
        <br />
        Телефон: {operator.phone}
        <br />
        Почтовый адрес: {operator.postalAddress}
        <br />
        Режим работы: Сервис доступен круглосуточно; обращения принимаются по электронной почте без
        ограничения по времени, ответ направляется в срок, установленный законом, а при его
        отсутствии — в течение {legalConfig.responseTerm}.
      </p>
    </Section>
  );
}

function LegalSources({ number }: { readonly number: string }): JSX.Element {
  return (
    <Section title={`${number}. Нормативная основа`}>
      <ul>
        <li>
          <a
            href="https://www.consultant.ru/document/cons_doc_LAW_61801/"
            target="_blank"
            rel="noreferrer"
          >
            Федеральный закон № 152-ФЗ «О персональных данных»
          </a>
        </li>
        <li>
          <a
            href="https://www.consultant.ru/document/cons_doc_LAW_305/"
            target="_blank"
            rel="noreferrer"
          >
            Закон РФ № 2300-1 «О защите прав потребителей»
          </a>
        </li>
        <li>
          <a
            href="https://pd.rkn.gov.ru/operators-registry/notification/"
            target="_blank"
            rel="noreferrer"
          >
            Уведомление Роскомнадзора об обработке персональных данных
          </a>
        </li>
        <li>
          <a
            href="https://pd.rkn.gov.ru/cross-border-transmission/form2/"
            target="_blank"
            rel="noreferrer"
          >
            Уведомление Роскомнадзора о трансграничной передаче
          </a>
        </li>
      </ul>
    </Section>
  );
}

export const myloreDocuments: readonly LegalDocument[] = [
  {
    id: 'offer',
    pageTitle: 'Публичная оферта на оказание услуг',
    title: 'Публичная оферта',
    summary: 'Условия заказа и создания персонализированной электронной книги.',
    render: (context) => <Offer {...context} />,
  },
  {
    id: 'privacy',
    pageTitle: 'Политика обработки персональных данных',
    title: 'Политика обработки персональных данных',
    summary: 'Какие данные обрабатываются, зачем, как хранятся и удаляются.',
    render: (context) => <Privacy {...context} />,
  },
  {
    id: 'photo-consent',
    pageTitle: 'Согласие на обработку фотографий и данных изображённых лиц',
    title: 'Согласие на обработку фотографий',
    summary: 'Отдельные условия работы с фотографиями ребёнка и других людей.',
    render: (context) => <PhotoConsent {...context} />,
  },
  {
    id: 'voice-consent',
    pageTitle: 'Согласие на обработку записи голоса и создание синтетической речи',
    title: 'Согласие на обработку записи голоса',
    summary: 'Условия создания голосового образца и озвучивания книги.',
    render: () => <VoiceConsent />,
  },
  {
    id: 'publication-consent',
    pageTitle: 'Согласие на публикацию персональной книги по открытой ссылке',
    title: 'Согласие на публикацию книги',
    summary: 'Условия открытия доступа к книге по публичной ссылке и прекращения публикации.',
    render: () => <PublicationConsent />,
  },
  {
    id: 'payment',
    pageTitle: 'Оплата, получение книги, отказ и возврат денежных средств',
    title: 'Оплата, получение и возвраты',
    summary: 'Как проходит оплата, когда будет готова книга и как запросить возврат.',
    render: (context) => <Payment {...context} />,
  },
  {
    id: 'cookies',
    pageTitle: 'Уведомление об использовании cookie и локального хранилища',
    title: 'Использование cookie',
    summary: 'Какие технические cookie нужны сайту и как ими управлять.',
    render: () => <Cookies />,
  },
];
